import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  Badge,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  ErrorState,
  Field,
  LoadingState,
  Select,
  TextArea,
  TextInput,
  formatDate,
} from "@odingard/design-system";
import type { PolicyVersion } from "@odingard/sdk-client";
import { useAuth } from "../../lib/auth";
import { useAsync } from "../../lib/useAsync";
import { api, policyApi } from "../../lib/api";
import { PolicyDemoBanner } from "../../components/PolicyMode";

function isoInDays(days: number): string {
  return new Date(Date.now() + days * 86400000).toISOString();
}

export function PolDraftDetail() {
  const { draftId = "" } = useParams();
  const { activeOrg, user } = useAuth();
  const orgId = activeOrg?.id ?? "";
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [sectionKey, setSectionKey] = useState("");
  const [heading, setHeading] = useState("");
  const [body, setBody] = useState("");
  const [reviewerId, setReviewerId] = useState("");
  const [email, setEmail] = useState("");
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [rationale, setRationale] = useState("");

  const state = useAsync(
    async () => {
      const [draft, reviewers, members] = await Promise.all([
        policyApi.draft(orgId, draftId),
        policyApi.externalReviewers(orgId, draftId),
        api.members(orgId),
      ]);
      // Versions belong to the policy, so the newest one cut from this draft is
      // the governance subject; its reviews drive approval and publication.
      const policyId = draft.data.draft.policyId;
      const versions = policyId ? (await policyApi.versions(orgId, policyId)).data.versions : [];
      const latest = versions.find((v) => v.draftId === draftId) ?? versions[0] ?? null;
      const reviews = latest ? (await policyApi.reviews(orgId, latest.id)).data.reviews : [];
      return { draft, reviewers, version: latest, reviews, members: members.members };
    },
    [orgId, draftId],
  );

  const draft = state.data?.draft.data.draft;
  const reviewers = state.data?.reviewers.data.reviewers ?? [];
  const version: PolicyVersion | null = state.data?.version ?? null;
  const reviews = state.data?.reviews ?? [];
  const members = state.data?.members ?? [];
  const openReview = reviews.find((r) => r.status === "in_review") ?? null;

  async function run(label: string, fn: () => Promise<void>) {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await fn();
      setNotice(label);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Request failed.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div className="page-head">
        <div>
          <h1>{draft?.title ?? "Draft"}</h1>
          <p>Sections, immutable versions, governed review and external reviewer links.</p>
        </div>
      </div>

      <PolicyDemoBanner show={state.data?.draft.demoMode ?? false} />
      {error && <ErrorState description={error} />}
      {notice && (
        <div className="ods-failclosed" role="status" style={{ borderColor: "var(--ods-color-success)" }}>
          {notice}
        </div>
      )}
      {state.loading && <LoadingState label="Loading draft…" />}
      {state.error && <ErrorState description={state.error.message} onRetry={state.reload} />}

      {draft && (
        <>
          <Card>
            <CardHeader title="Sections" action={<Badge tone="neutral">{draft.status}</Badge>} />
            <CardBody>
              {draft.sections.length === 0 ? (
                <EmptyState title="No sections" description="Add the first section to begin authoring." />
              ) : (
                <ul className="ods-stack" style={{ paddingLeft: 0, listStyle: "none" }}>
                  {draft.sections.map((s) => (
                    <li key={s.id}>
                      <div className="ods-row" style={{ gap: "var(--ods-space-2)" }}>
                        <strong style={{ fontWeight: 600 }}>{s.heading}</strong>
                        {s.aiGenerated ? <Badge tone="warning">AI generated</Badge> : null}
                      </div>
                      <p style={{ margin: "4px 0 0", color: "var(--ods-color-muted)" }}>{s.body}</p>
                    </li>
                  ))}
                </ul>
              )}
              <div className="ods-row" style={{ alignItems: "flex-end", gap: "var(--ods-space-3)", flexWrap: "wrap", marginTop: "var(--ods-space-3)" }}>
                <Field label="Section key">
                  {(id) => (
                    <TextInput id={id} value={sectionKey} onChange={(e) => setSectionKey(e.target.value)} placeholder="purpose" />
                  )}
                </Field>
                <Field label="Heading">
                  {(id) => (
                    <TextInput id={id} value={heading} onChange={(e) => setHeading(e.target.value)} placeholder="Purpose" />
                  )}
                </Field>
                <Field label="Body">
                  {(id) => (
                    <TextArea id={id} value={body} onChange={(e) => setBody(e.target.value)} rows={3} />
                  )}
                </Field>
                <Button
                  size="sm"
                  disabled={busy || !sectionKey.trim() || !heading.trim()}
                  onClick={() =>
                    run("Section added.", async () => {
                      await policyApi.addSection(orgId, draftId, {
                        sectionKey: sectionKey.trim(),
                        heading: heading.trim(),
                        body,
                      });
                      setSectionKey("");
                      setHeading("");
                      setBody("");
                      state.reload();
                    })
                  }
                >
                  Add section
                </Button>
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="Version & governed review" />
            <CardBody>
              <div className="ods-stack">
                <p style={{ margin: 0, color: "var(--ods-color-muted)" }}>
                  Cutting a version freezes the draft content with a content hash. Publication requires an
                  approval, and the author may not approve their own version.
                </p>
                {version && (
                  <div className="ods-row" style={{ gap: "var(--ods-space-2)" }}>
                    <Badge tone="neutral">v{version.versionNumber}</Badge>
                    <span style={{ color: "var(--ods-color-muted)" }}>{version.contentHash}</span>
                    <Badge tone="neutral">{version.status}</Badge>
                  </div>
                )}
                <div className="ods-row" style={{ gap: "var(--ods-space-2)", flexWrap: "wrap" }}>
                  <Button
                    size="sm"
                    disabled={busy}
                    onClick={() =>
                      run("Version created.", async () => {
                        await policyApi.createVersion(orgId, draftId);
                        state.reload();
                      })
                    }
                  >
                    Cut version
                  </Button>
                  <Field label="Reviewer">
                    {(id) => (
                      <Select id={id} value={reviewerId} onChange={(e) => setReviewerId(e.target.value)}>
                        <option value="">Select a teammate…</option>
                        {members.map((m) => (
                          <option key={m.id} value={m.id}>
                            {m.displayName || m.email}
                            {m.id === user?.id ? " (you)" : ""}
                          </option>
                        ))}
                      </Select>
                    )}
                  </Field>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy || !version || !reviewerId.trim()}
                    onClick={() =>
                      run("Submitted for review.", async () => {
                        await policyApi.submitForReview(orgId, version!.id, {
                          reviewerIds: [reviewerId.trim()],
                          requiredReviewerIds: [reviewerId.trim()],
                          mode: "parallel",
                        });
                        state.reload();
                      })
                    }
                  >
                    Submit for review
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy || !version || version.status !== "reviewed"}
                    onClick={() =>
                      run("Version approved.", async () => {
                        await policyApi.approveVersion(orgId, version!.id, rationale.trim());
                        state.reload();
                      })
                    }
                  >
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="secondary"
                    disabled={busy || !version}
                    onClick={() =>
                      run("Version published.", async () => {
                        await policyApi.publishVersion(orgId, version!.id, { effectiveDate: null });
                        state.reload();
                      })
                    }
                  >
                    Publish
                  </Button>
                </div>
                <Field label="Decision rationale">
                  {(id) => <TextInput id={id} value={rationale} onChange={(e) => setRationale(e.target.value)} />}
                </Field>
                {openReview && (
                  <div className="ods-stack" data-testid="open-review">
                    <div className="ods-row" style={{ gap: "var(--ods-space-2)", alignItems: "center" }}>
                      <strong style={{ fontWeight: 600 }}>Review in progress</strong>
                      <Badge tone="warning">{openReview.status}</Badge>
                      <span style={{ color: "var(--ods-color-muted)" }}>step {openReview.currentStep}</span>
                    </div>
                    <div className="ods-row" style={{ gap: "var(--ods-space-2)" }}>
                      <Button
                        size="sm"
                        disabled={busy}
                        onClick={() =>
                          run("Review decision recorded.", async () => {
                            await policyApi.recordReviewDecision(orgId, openReview.id, {
                              decision: "approved",
                              rationale: rationale.trim(),
                            });
                            state.reload();
                          })
                        }
                      >
                        Record approval
                      </Button>
                      <Button
                        size="sm"
                        variant="secondary"
                        disabled={busy}
                        onClick={() =>
                          run("Changes requested.", async () => {
                            await policyApi.recordReviewDecision(orgId, openReview.id, {
                              decision: "changes_requested",
                              rationale: rationale.trim(),
                            });
                            state.reload();
                          })
                        }
                      >
                        Request changes
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            </CardBody>
          </Card>

          <Card>
            <CardHeader title="External review" />
            <CardBody>
              <div className="ods-stack">
                <p style={{ margin: 0, color: "var(--ods-color-muted)" }}>
                  Invitations issue a revocable link token that expires. Only its hash is stored, so the link is
                  shown once, here.
                </p>
                <div className="ods-row" style={{ alignItems: "flex-end", gap: "var(--ods-space-3)", flexWrap: "wrap" }}>
                  <Field label="Reviewer email">
                    {(id) => (
                      <TextInput id={id} value={email} onChange={(e) => setEmail(e.target.value)} placeholder="counsel@example.com" />
                    )}
                  </Field>
                  <Button
                    size="sm"
                    disabled={busy || !email.trim()}
                    onClick={() =>
                      run("External reviewer invited.", async () => {
                        const res = await policyApi.inviteExternalReviewer(orgId, draftId, {
                          email: email.trim(),
                          displayName: email.trim(),
                          canComment: true,
                          expiresAt: isoInDays(14),
                        });
                        setLinkToken(res.data.token);
                        setEmail("");
                        state.reload();
                      })
                    }
                  >
                    Invite reviewer
                  </Button>
                </div>
                {linkToken && (
                  <div className="ods-failclosed" role="status">
                    <strong>Review link (shown once):</strong>{" "}
                    <code data-testid="external-review-link">{`${window.location.origin}/policy-review?token=${linkToken}`}</code>
                  </div>
                )}
                {reviewers.length === 0 ? (
                  <EmptyState title="No external reviewers" description="Invite outside counsel or an auditor to review this draft." />
                ) : (
                  <ul className="ods-stack" style={{ paddingLeft: 0, listStyle: "none" }}>
                    {reviewers.map((r) => (
                      <li key={r.id} className="ods-row" style={{ gap: "var(--ods-space-2)", alignItems: "center" }}>
                        <strong style={{ fontWeight: 600 }}>{r.email}</strong>
                        <Badge tone={r.status === "revoked" ? "danger" : "neutral"}>{r.status}</Badge>
                        <span style={{ color: "var(--ods-color-muted)" }}>expires {formatDate(r.expiresAt)}</span>
                        {r.status !== "revoked" && (
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={busy}
                            onClick={() =>
                              run("Reviewer access revoked.", async () => {
                                await policyApi.revokeExternalReviewer(orgId, r.id);
                                state.reload();
                              })
                            }
                          >
                            Revoke
                          </Button>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </CardBody>
          </Card>
        </>
      )}
    </>
  );
}
